import { useEffect, useState } from "react"

export const COMPACT_NAV_MEDIA_QUERY =
  "(max-width: 1023px), (max-height: 599px)"

let frozenCompactLayout: boolean | null = null

type FullscreenCapableElement = Element & {
  webkitRequestFullscreen?: () => Promise<void> | void
  webkitRequestFullScreen?: () => Promise<void> | void
  msRequestFullscreen?: () => Promise<void> | void
}

type WebkitVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void
  webkitExitFullscreen?: () => void
  webkitDisplayingFullscreen?: boolean
}

type FullscreenCapableDocument = Document & {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
  webkitCancelFullScreen?: () => Promise<void> | void
  msExitFullscreen?: () => Promise<void> | void
  msFullscreenElement?: Element | null
}

function readIsViewportCompact() {
  if (typeof window === "undefined") return false
  return window.matchMedia(COMPACT_NAV_MEDIA_QUERY).matches
}

/** Freeze mobile/desktop layout before entering fullscreen. */
export function lockCompactNavigationLayout() {
  frozenCompactLayout = readIsViewportCompact()
}

export function unlockCompactNavigationLayout() {
  frozenCompactLayout = null
}

function getElementRequestFullscreen(element: Element) {
  const el = element as FullscreenCapableElement
  if (typeof el.requestFullscreen === "function") {
    return () => el.requestFullscreen()
  }
  if (typeof el.webkitRequestFullscreen === "function") {
    return () => el.webkitRequestFullscreen!()
  }
  if (typeof el.webkitRequestFullScreen === "function") {
    return () => el.webkitRequestFullScreen!()
  }
  if (typeof el.msRequestFullscreen === "function") {
    return () => el.msRequestFullscreen!()
  }
  return null
}

function findWebkitFullscreenVideo(root: Element): WebkitVideoElement | null {
  const candidates: Element[] = [
    root,
    ...Array.from(root.querySelectorAll("video")),
  ]

  for (const candidate of candidates) {
    if (!(candidate instanceof HTMLVideoElement)) continue
    const video = candidate as WebkitVideoElement
    if (typeof video.webkitEnterFullscreen === "function") {
      return video
    }
  }

  return null
}

function listenForWebkitVideoFullscreenEnd(video: WebkitVideoElement) {
  const onEnd = () => {
    unlockCompactNavigationLayout()
    video.removeEventListener("webkitendfullscreen", onEnd)
  }
  video.addEventListener("webkitendfullscreen", onEnd)
}

export async function requestElementFullscreen(element: Element) {
  lockCompactNavigationLayout()
  try {
    const requestFullscreen = getElementRequestFullscreen(element)
    if (requestFullscreen) {
      await requestFullscreen()
      return
    }

    // iPhone Safari: only <video> can go fullscreen via webkitEnterFullscreen.
    const video = findWebkitFullscreenVideo(element)
    if (video) {
      listenForWebkitVideoFullscreenEnd(video)
      video.webkitEnterFullscreen!()
      return
    }

    throw new Error("Fullscreen is not supported on this element")
  } catch (error) {
    unlockCompactNavigationLayout()
    throw error
  }
}

export async function exitElementFullscreen() {
  const doc = document as FullscreenCapableDocument

  if (typeof doc.exitFullscreen === "function" && doc.fullscreenElement) {
    await doc.exitFullscreen()
    return
  }
  if (
    typeof doc.webkitExitFullscreen === "function" &&
    doc.webkitFullscreenElement
  ) {
    await doc.webkitExitFullscreen()
    return
  }
  if (
    typeof doc.webkitCancelFullScreen === "function" &&
    doc.webkitFullscreenElement
  ) {
    await doc.webkitCancelFullScreen()
    return
  }
  if (typeof doc.msExitFullscreen === "function" && doc.msFullscreenElement) {
    await doc.msExitFullscreen()
    return
  }

  const videos = document.querySelectorAll("video")
  for (const node of videos) {
    const video = node as WebkitVideoElement
    if (
      video.webkitDisplayingFullscreen &&
      typeof video.webkitExitFullscreen === "function"
    ) {
      video.webkitExitFullscreen()
      return
    }
  }
}

export function getFullscreenElement(): Element | null {
  const doc = document as FullscreenCapableDocument
  if (doc.fullscreenElement) return doc.fullscreenElement
  if (doc.webkitFullscreenElement) return doc.webkitFullscreenElement
  if (doc.msFullscreenElement) return doc.msFullscreenElement

  const videos = document.querySelectorAll("video")
  for (const node of videos) {
    const video = node as WebkitVideoElement
    if (video.webkitDisplayingFullscreen) return video
  }

  return null
}

/**
 * App-level owner: mount once (e.g. AppProviders) so freeze clears on
 * fullscreen exit even when slide routes remount useCompactNavigation.
 */
export function useCompactNavigationFullscreenSync() {
  useEffect(() => {
    const onFullscreenChange = () => {
      if (!getFullscreenElement()) {
        unlockCompactNavigationLayout()
      }
    }

    document.addEventListener("fullscreenchange", onFullscreenChange)
    document.addEventListener("webkitfullscreenchange", onFullscreenChange)
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange)
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange)
    }
  }, [])
}

export function useCompactNavigation() {
  const [matches, setMatches] = useState<boolean | undefined>(undefined)
  useEffect(() => {
    try {
      if ("matchMedia" in window) {
        const mediaQuery = window.matchMedia(COMPACT_NAV_MEDIA_QUERY)
        setMatches(mediaQuery.matches)
        const callback = (event: MediaQueryListEvent) =>
          setMatches(event.matches)
        mediaQuery.addEventListener("change", callback)
        return () => {
          mediaQuery.removeEventListener("change", callback)
        }
      }
    } catch (e) {
      // Safari iframe compatibility issue
      return undefined
    }
  }, [])

  if (frozenCompactLayout !== null) {
    return frozenCompactLayout
  }

  if (matches === undefined) {
    return true
  }
  return matches
}
